import { motion, useReducedMotion, useIsPresent } from "motion/react"
import { TagsIcon, UserCircleIcon, XIcon } from "lucide-react"
import { Button } from "~/components/ui/button"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from "~/components/ui/command"
import { Spinner } from "~/components/ui/spinner"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { usePermission } from "~/hooks/usePermission"
import { IssueDifficulty, IssuePriority, IssueStatus } from "~/lib/enums"
import { TeamPermissions } from "~/lib/permissions"
import { api, type RouterInputs, type RouterOutputs } from "~/trpc/react"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon } from "../icons/priority"
import { StatusIcon } from "../icons/status"
import { IssueLabel } from "../issueDetailsPage/IssueLabel"
import {
	IssueSetting,
	issueSettingAssignees
} from "../issueDetailsPage/IssueSetting"

export function IssueBulkActions({
	issues,
	selectableCount,
	pending,
	onSelectAll,
	onClear,
	onUpdate
}: {
	issues: RouterOutputs["issues"]["getTeamIssues"]["issues"]
	selectableCount: number
	pending: boolean
	onSelectAll: () => void
	onClear: () => void
	onUpdate: (
		change: Omit<RouterInputs["issues"]["bulkUpdate"], "teamId" | "issueIds">
	) => void
}) {
	const reduceMotion = useReducedMotion()
	const isPresent = useIsPresent()
	const teamId = issues[0]?.teamId ?? ""
	const { hasTeamPermission, teamLoaded } = usePermission({
		activeTeamId: teamId
	})
	const { data: team } = api.team.get.useQuery(
		{ teamId },
		{ enabled: !!teamId }
	)
	const { data: labels } = api.labels.list.useQuery(
		{ teamId },
		{ enabled: !!teamId }
	)
	const disabled =
		!isPresent ||
		pending ||
		!teamLoaded ||
		!hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const labelOptions = (labels ?? []).map((label) => ({
		value: label.id,
		label: label.name,
		display: <IssueLabel label={label} />
	}))
	const selectedLabelIds = new Set(
		issues.flatMap((issue) => issue.labels.map((label) => label.id))
	)

	return (
		<motion.div
			initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
			transition={{ duration: reduceMotion ? 0 : 0.18, ease: "easeOut" }}
			inert={!isPresent}
			role="region"
			aria-label="Bulk issue actions"
			aria-busy={pending}
			className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-stroke bg-background-tertiary p-3 surface-raised"
		>
			<span role="status" className="text-sm font-medium text-text-main">
				{issues.length} selected
			</span>
			{pending && <Spinner aria-label="Updating selected issues" />}
			<Button
				variant="ghost"
				size="compact"
				disabled={disabled || issues.length >= Math.min(selectableCount, 100)}
				onClick={onSelectAll}
			>
				{selectableCount > 100 ? "Select up to 100" : "Select all in team"}
			</Button>
			<IssueSetting<IssueStatus | null>
				value={null}
				options="status"
				activeTeamId={teamId}
				disabled={disabled}
				showBorder
				size="compact"
				triggerClassName="w-auto"
				ariaLabel="Change selected issues' status"
				placeholder={
					<>
						<StatusIcon status={IssueStatus.ToDo} />
						<span>Status</span>
					</>
				}
				onChange={(status) => {
					if (status) onUpdate({ status })
				}}
			/>
			<IssueSetting<IssuePriority | null>
				value={null}
				options="priority"
				activeTeamId={teamId}
				disabled={disabled}
				showBorder
				size="compact"
				triggerClassName="w-auto"
				ariaLabel="Change selected issues' priority"
				placeholder={
					<>
						<PriorityIcon priority={IssuePriority.None} />
						<span>Priority</span>
					</>
				}
				onChange={(priority) => {
					if (priority) onUpdate({ priority })
				}}
			/>
			<IssueSetting<IssueDifficulty | null>
				value={null}
				options="difficulty"
				activeTeamId={teamId}
				disabled={disabled}
				showBorder
				size="compact"
				triggerClassName="w-auto"
				ariaLabel="Change selected issues' difficulty"
				trigger={
					<>
						<DifficultyIcon difficulty={1} />
						<span>Difficulty</span>
					</>
				}
				onChange={(difficulty) => {
					onUpdate({ difficulty })
				}}
			/>
			<IssueSetting<string | null>
				value={null}
				options={issueSettingAssignees(team?.teamMembers)}
				activeTeamId={teamId}
				disabled={disabled || !team}
				withSearch
				showBorder
				size="compact"
				triggerClassName="w-auto"
				ariaLabel="Assign selected issues"
				placeholder={
					<>
						<UserCircleIcon />
						<span>Assignee</span>
					</>
				}
				onChange={(assigneeId) => onUpdate({ assigneeId })}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						size="compact"
						disabled={disabled || !labels?.length}
					>
						<TagsIcon /> Labels
					</Button>
				</PopoverTrigger>
				<PopoverContent
					layout="panel"
					align="end"
					aria-label="Change selected issues' labels"
				>
					<Command>
						<CommandInput placeholder="Search labels…" />
						<CommandList>
							<CommandEmpty>No labels found</CommandEmpty>
							<CommandGroup heading="Add to selected issues">
								{labelOptions.map((label) => (
									<CommandItem
										key={label.value}
										value={`add-${label.value}-${label.label}`}
										disabled={disabled}
										onSelect={() =>
											onUpdate({
												labels: { mode: "add", labelIds: [label.value] }
											})
										}
									>
										{label.display}
									</CommandItem>
								))}
							</CommandGroup>
							<CommandGroup heading="Remove from selected issues">
								{labelOptions
									.filter((label) => selectedLabelIds.has(label.value))
									.map((label) => (
										<CommandItem
											key={label.value}
											value={`remove-${label.value}-${label.label}`}
											disabled={disabled}
											onSelect={() =>
												onUpdate({
													labels: { mode: "remove", labelIds: [label.value] }
												})
											}
										>
											{label.display}
										</CommandItem>
									))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
			<Button
				variant="ghost"
				size="icon-xs"
				className="ml-auto"
				disabled={pending}
				onClick={onClear}
				aria-label="Clear issue selection"
			>
				<XIcon />
			</Button>
		</motion.div>
	)
}
